// src/components/home/Home.js
import React, { useEffect, useState, useMemo } from 'react';
import { Video, MessageCircle, CheckCircle, Moon, Utensils } from 'lucide-react';
import StatsCard from './StatsCard';
import UpdateCard from './UpdateCard';
import AnnouncementBanner from './AnnouncementBanner';

import { useAtom } from 'jotai';
import { activeChildDetail, appConstants, ChildTimeLineAtom } from '../../atomstore/store';
import { useRequestProcessor } from '../../requestProcessor';
import { fetchChildTimeline } from '../../queryfunctions/queryfunctions';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import SpinnerLoading from '../common/SpinnerLoading';

export const SERVER_DATE_FORMAT = "YYYY-MM-DD";

const Home = ({ setSelectedPhoto }) => {
    const [showMoreUpdates, setShowMoreUpdates] = useState(false);
    const [activeChild] = useAtom(activeChildDetail);
    const [ChildTimeline, setTimeline] = useAtom(ChildTimeLineAtom);
    const [childTimeline, setChildTimeline] = useState({ startIndex: 0 });
    const [constants] = useAtom(appConstants);
    const [, setDataAvailable] = useState(true);
    const [, setLength] = useState(0);
    const [isAbsent, setIsAbsent] = useState(false);
    const navigate = useNavigate()
    const [stats, setStats] = useState({
        checkin: '',
        naps: '',
        meals: '',
    })
    const [selectedDate, setSelectedDate] = useState(
        moment().format(SERVER_DATE_FORMAT)
    );

    const { useQueryWrapper } = useRequestProcessor();

    const ITEM_PER_PAGE = 50;

    // helper: find timeline group object that corresponds to selectedDate
    const findTimelineGroupForDate = (timelineArray = [], dateString) => {
        if (!Array.isArray(timelineArray)) return null;
        const target = moment(dateString, SERVER_DATE_FORMAT);
        return timelineArray.find((group) => {
            if (!group?.dateGroup) return false;
            // Accept both DD-MM-YY and DD-MM-YYYY
            const parsed = moment(group.dateGroup, ["DD-MM-YY", "DD-MM-YYYY", "DD-MM-YYYY"], true);
            if (!parsed.isValid()) {
                // try non-strict parse as fallback
                const parsedLoose = moment(group.dateGroup, ["DD-MM-YY", "DD-MM-YYYY"]);
                return parsedLoose.isValid() && parsedLoose.isSame(target, 'day');
            }
            return parsed.isSame(target, 'day');
        }) || null;
    };


    const computeStats = (timelineData = []) => {
        let checkinTime = null;
        let mealCount = 0;
        let absentDetected = false;

        let totalNapMinutes = 0;
        let sleepStart = null;

        const todayString = moment().format("YYYY-MM-DD");

        // Map items to include parsed message time
        const itemsWithMessageTime = timelineData
            .filter(item => item.date && moment(item.date).isSame(todayString, 'day'))
            .map(item => {
                let messageTime = null;
                const timeMatch = item.message?.match(/(\d{1,2}:\d{2})/);
                if (timeMatch) {
                    messageTime = moment(`${moment(item.date).format("YYYY-MM-DD")} ${timeMatch[1]}`, "YYYY-MM-DD HH:mm");
                }
                return { ...item, messageTime };
            });

        // Sort items by messageTime if available, else fallback to `time` field
        const sortedData = itemsWithMessageTime.sort((a, b) => {
            if (a.messageTime && b.messageTime) return a.messageTime.diff(b.messageTime);
            if (a.time && b.time) return moment(a.time, "hh:mm a").diff(moment(b.time, "hh:mm a"));
            return 0;
        });

        sortedData.forEach((item, idx) => {
            const { type, message, time, messageTime } = item;
            const lowerMsg = message?.toLowerCase() || "";

            /* Attendance */
            if (type === "Attendance") {
                if (lowerMsg.includes("absent today")) absentDetected = true;
                else if (time) checkinTime = time;
            }

            /* Meals */
            if (["Breakfast", "Lunch", "Snacks", "Fruits"].includes(type)) mealCount += 1;

            /* Sleep */
            if (type === "Sleep" && messageTime) {
                if (lowerMsg.includes("slept")) {
                    sleepStart = messageTime;
                }
                if (lowerMsg.includes("woke") && sleepStart) {
                    let diff = messageTime.diff(sleepStart, "minutes");
                    if (diff < 0) diff += 24 * 60;
                    if (diff > 0) totalNapMinutes += diff;
                    sleepStart = null;
                }
            }
        });

        // Format nap duration
        const hours = Math.floor(totalNapMinutes / 60);
        const minutes = totalNapMinutes % 60;
        const napDuration = totalNapMinutes > 0 ? (hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`) : "--:--";

        return {
            checkin: checkinTime || "--:--",
            naps: napDuration,
            meals: mealCount > 0 ? `${mealCount} served` : "0 served",
            isAbsent: absentDetected,
        };
    };







    // derive today's group and previous groups (memoized)
    const { todayGroup, previousGroups } = useMemo(() => {
        const tg = findTimelineGroupForDate(ChildTimeline, selectedDate);
        const prev = Array.isArray(ChildTimeline)
            ? ChildTimeline.filter((g) => g !== tg)
            : [];
        return { todayGroup: tg, previousGroups: prev };
    }, [ChildTimeline, selectedDate]);

    // whenever ChildTimeline or selectedDate changes, recompute stats for today's group
    useEffect(() => {
        const dataForStats = todayGroup?.data || [];
        const childStats = computeStats(dataForStats);
        setStats(childStats);
        setIsAbsent(childStats.isAbsent);
    }, [ChildTimeline, selectedDate, todayGroup]);

    // same logic as in ChildTimeline fetch
    const { isLoading, isFetching, refetch } = useQueryWrapper(
        ["ChildTimeline", "timeline" + activeChild?.id, childTimeline, selectedDate],
        () =>
            fetchChildTimeline(
                childTimeline.startIndex,
                setDataAvailable,
                ChildTimeline,
                ITEM_PER_PAGE,
                constants?.INCLUDE_TYPES,
                setLength,
                setTimeline,
                selectedDate
            ),
        { keepPreviousData: true },
        {
            onSuccess: (data) => {
                if (data?.status) {
                    console.error("Timeline fetch error", data?.error);
                } else {
                    setChildTimeline(data);
                    if (Array.isArray(data) && data[0]?.data) {
                        // do not assume data[0] is today — compute stats from the matching group instead
                        const matched = findTimelineGroupForDate(data, selectedDate);
                        const childStats = computeStats(matched?.data || []);
                        setStats(childStats);
                    } else {
                        console.warn("No valid timeline data for stats");
                    }
                }
            },
        }
    );

    const loadMore = () => {
        setChildTimeline((prev) => ({
            ...prev,
            startIndex: (prev.startIndex || 0) + ITEM_PER_PAGE,
        }));
    };

    // If absent for selected date show absent UI
    if (isAbsent) {
        return (
            <div className="p-4 space-y-4">
                <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border-2 border-orange-300 rounded-3xl p-8 text-center">
                    <div className="text-6xl mb-4">🏠</div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">
                        {activeChild?.name} is absent today
                    </h3>
                    <p className="text-gray-600">We hope to see them soon!</p>
                </div>

                <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-3xl p-6">
                    <h3 className="text-white text-lg font-semibold mb-4">
                        Still want to stay connected?
                    </h3>
                    <div className="space-y-3">
                        <button
                            onClick={() => navigate('/cctv')}
                            className="w-full bg-white text-cyan-600 py-3 rounded-2xl font-semibold flex items-center justify-center space-x-2"
                        >
                            <Video className="w-5 h-5" />
                            <span>Watch Live CCTV</span>
                        </button>
                        <button
                            onClick={() => navigate('/chat')}
                            className="w-full bg-cyan-400 text-white py-3 rounded-2xl font-semibold flex items-center justify-center space-x-2"
                        >
                            <MessageCircle className="w-5 h-5" />
                            <span>Chat with Teacher</span>
                        </button>
                    </div>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-4">Previous Day Updates</h3>
                <div className="space-y-4">
                    {previousGroups && previousGroups.length > 0 ? (
                        previousGroups.map((dayGroup, dayIdx) => (
                            <div
                                key={dayIdx}
                                className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100"
                            >
                                <h3 className="font-bold text-gray-800 mb-3">
                                    {moment(dayGroup.dateGroup, ["DD-MM-YY", "DD-MM-YYYY"]).format("MMMM DD, YYYY")}
                                </h3>

                                {dayGroup.data && dayGroup.data.length > 0 ? (
                                    <div className="space-y-3">
                                        {dayGroup.data.map((update, idx) => (
                                            <UpdateCard
                                                key={idx}
                                                update={update}
                                                setSelectedPhoto={setSelectedPhoto}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="bg-gray-50 rounded-xl p-4 text-center">
                                        <p className="text-gray-600">
                                            No updates for this day
                                        </p>
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        <div className="bg-gray-50 rounded-xl p-4 text-center">
                            <p className="text-gray-600">No previous day updates found</p>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 space-y-4">
            {isLoading && <SpinnerLoading />}
            {/* Welcome Banner */}
            <div className="bg-gradient-to-r from-cyan-500 to-blue-600 rounded-3xl p-6 flex items-center justify-between">
                <div className="text-white">
                    <p className="text-sm opacity-90 mb-1">Welcome back</p>
                    <h2 className="text-xl md:text-2xl font-bold mb-1">{activeChild?.name} {activeChild?.last_name}</h2>
                    <p className="text-sm opacity-90">{activeChild?.["room.name"]}</p>
                </div>
                <button
                    onClick={() => navigate('/cctv')}
                    className="bg-white text-cyan-600 px-6 py-3 rounded-2xl font-semibold flex items-center space-x-2 shadow-lg"
                >
                    <Video className="w-5 h-5" />
                    <span>Watch Live</span>
                </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-3 gap-3">
                <StatsCard
                    icon={CheckCircle}
                    label="Check-in / Check-out"
                    value={stats?.checkin}
                    gradient="from-blue-400 to-blue-600"
                    bgGradient="from-blue-50 to-blue-100"
                    borderColor="border-blue-200"
                    textColor="text-blue-600"
                    onClick={() => navigate('/checkin-history')}
                />
                <StatsCard
                    icon={Moon}
                    label="Naps"
                    value={stats?.naps}
                    gradient="from-purple-400 to-purple-600"
                    bgGradient="from-purple-50 to-purple-100"
                    borderColor="border-purple-200"
                    textColor="text-purple-600"
                    onClick={() => document.getElementById('nap-section')?.scrollIntoView({ behavior: 'smooth' })}
                />
                <StatsCard
                    icon={Utensils}
                    label="Meal"
                    value={stats?.meals}
                    gradient="from-orange-400 to-orange-600"
                    bgGradient="from-orange-50 to-orange-100"
                    borderColor="border-orange-200"
                    textColor="text-orange-600"
                    onClick={() =>
                        navigate('/schedule', {
                            state: { activeTab: 'menu' }
                        })
                    }
                />
            </div>

            {/* Announcement Banner */}
            {/* <AnnouncementBanner
                childName={activeChild?.name}
            /> */}

            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-800">Today's Updates</h3>
                </div>

                <div className="space-y-4">
                    {todayGroup && Array.isArray(todayGroup.data) && todayGroup.data.length > 0 ? (
                        todayGroup.data.map((update, idx) => (
                            <UpdateCard
                                key={idx}
                                update={update}
                                setSelectedPhoto={setSelectedPhoto}
                            />
                        ))
                    ) : (
                        <div className="bg-gray-50 rounded-xl p-4 text-center">
                            <p className="text-gray-600">No updates for today</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Previous Days Updates (toggle) */}
            {showMoreUpdates && (
                <div className="space-y-4">
                    {previousGroups && previousGroups.length > 0 ? (
                        previousGroups.map((dayGroup, dayIdx) => (
                            <div
                                key={dayIdx}
                                className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100"
                            >
                                <h3 className="font-bold text-gray-800 mb-3">
                                    {moment(dayGroup.dateGroup, ["DD-MM-YY", "DD-MM-YYYY"]).format("MMMM DD, YYYY")}
                                </h3>

                                {dayGroup.data && dayGroup.data.length > 0 ? (
                                    <div className="space-y-3">
                                        {dayGroup.data.map((update, idx) => (
                                            <UpdateCard
                                                key={idx}
                                                update={update}
                                                setSelectedPhoto={setSelectedPhoto}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="bg-gray-50 rounded-xl p-4 text-center">
                                        <p className="text-gray-600">
                                            No updates for this day
                                        </p>
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        <div className="bg-gray-50 rounded-xl p-4 text-center">
                            <p className="text-gray-600">No previous day updates found</p>
                        </div>
                    )}
                </div>
            )}

            <button
                onClick={() => setShowMoreUpdates(!showMoreUpdates)}
                className="w-full bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-200 transition"
            >
                {showMoreUpdates ? 'Show Less' : 'Load Previous Days'}
            </button>
        </div>
    );
};

export default Home;
